import { Box, Button, HorizontalStack } from '@shopify/polaris';
import { ChevronDownMinor, ChevronUpMinor } from '@shopify/polaris-icons';
import { useState } from 'react';
import { useCurrency } from '../../../../customHooks/useCurrency';
import { displayInTz } from '../../../../helpers/dates';
import { TableShell } from './TableShell';
import {
  TABLE_BODY_TEXT,
  TABLE_CELL_BORDER,
  TABLE_CELL_PADDING,
  TABLE_DANGER,
  TABLE_HEADER_BG,
  TABLE_HEADER_TEXT_SOFT,
  TABLE_HEADER_TEXT_STRONG,
  TABLE_POSITIVE,
  TABLE_SUBTLE,
  TABLE_TOTAL_BG,
} from './tableStyles';
import { div, formatMoney, formatPct, formatUnits, isNeg } from './formatters';

// Columnas del informe de Series — mismo esquema que el PDF
// `pdfEfficacySeries.service.ts`. Cada columna define:
//  - `label1` / `label2?`: cabecera en una o dos líneas (la 2ª se pinta
//    pequeña y en gris, entre paréntesis).
//  - `meanBar?`: substring de `label1` sobre el que pintar la barra de
//    "media" (`x̄`). Marca visual de columna agregada/promedio.
//  - `align`: `'left'` (texto) o `'right'` (numérico).
//  - `sticky?`: si la columna se queda fija a la izquierda al hacer
//    scroll horizontal. Sólo para Foto y Serie-Color.
//  - `width`: ancho fijo en px (necesario para sticky y para mantener la
//    cuadrícula).
//  - `sortBy?`: campo del back por el que se ordena al click en header.
//  - `render(row, ctx)`: devuelve `{ line1, line2?, line1Negative?,
//    line2Negative?, dot?: 'green'|'red' }` — mismo shape que el PDF.
export const SERIES_COLUMNS = [
  {
    id: 'image',
    label1: '',
    align: 'left',
    sticky: true,
    width: 88,
    render: (r) => ({ kind: 'image', src: r.image, alt: r.family }),
  },
  {
    id: 'family',
    label1: 'Serie-Color',
    align: 'left',
    sticky: true,
    width: 120,
    sortBy: 'family',
    render: (r) => ({
      // Link a la página del producto. Sin `productId` (fila TOTAL o
      // dato incompleto) cae al texto plano.
      line1: r.productId ? (
        <a
          className="efftable-link"
          href={`/products/${r.productId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {r.family ?? '—'}
        </a>
      ) : (
        (r.family ?? '—')
      ),
    }),
  },
  {
    id: 'entrada',
    label1: 'Entrada',
    label2: 'Servido',
    align: 'left',
    width: 110,
    sortBy: 'firstStoreEntryDate',
    render: (r, { timezone }) => ({
      line1: r.firstStoreEntryDate ? displayInTz(r.firstStoreEntryDate, timezone) : '—',
      line2: `${formatUnits(r.buyUnits)} uds`,
      line2Negative: isNeg(r.buyUnits),
    }),
  },
  {
    id: 'valor',
    label1: 'Valor venta',
    label2: 'Valor compra',
    align: 'right',
    width: 140,
    sortBy: 'sellValuePvfTotal',
    render: (r, { currency }) => {
      const venta = r.sellValuePvfTotal;
      let dot;
      if (r.buyValue > 0) dot = venta > r.buyValue ? 'green' : 'red';
      return {
        line1: formatMoney(venta, currency),
        line2: formatMoney(r.buyValue, currency),
        line1Negative: isNeg(venta),
        line2Negative: isNeg(r.buyValue),
        dot,
      };
    },
  },
  {
    id: 'pvpUnit',
    label1: 'PVP/ud',
    align: 'right',
    width: 90,
    sortBy: 'pvpUnit',
    render: (r, { currency }) => ({
      line1: formatMoney(r.pvpUnit, currency),
      line1Negative: isNeg(r.pvpUnit),
    }),
  },
  {
    id: 'pvpAvg',
    label1: 'PVP/ud',
    label2: 'Campaña',
    meanBar: 'PVP/ud',
    align: 'right',
    width: 100,
    sortBy: 'pvpAvgUnitTotal',
    render: (r, { currency }) => ({
      line1: formatMoney(r.pvpAvgUnitTotal, currency),
      line2: formatMoney(r.pvpAvgUnitInSeason, currency),
      line1Negative: isNeg(r.pvpAvgUnitTotal),
      line2Negative: isNeg(r.pvpAvgUnitInSeason),
    }),
  },
  {
    id: 'dto',
    label1: 'Dto.',
    label2: 'Campaña',
    meanBar: 'Dto.',
    align: 'right',
    width: 95,
    sortBy: 'discountAvgTotal',
    render: (r) => ({
      line1: formatPct(r.discountAvgTotal),
      line2: formatPct(r.discountAvgInSeason),
      line1Negative: isNeg(r.discountAvgTotal),
      line2Negative: isNeg(r.discountAvgInSeason),
    }),
  },
  {
    id: 'ventas',
    label1: 'Total Vtas.',
    label2: 'Campaña',
    align: 'right',
    width: 100,
    sortBy: 'sellUnitsTotal',
    render: (r) => ({
      line1: formatUnits(r.sellUnitsTotal),
      line2: formatUnits(r.sellUnitsInSeason),
      line1Negative: isNeg(r.sellUnitsTotal),
      line2Negative: isNeg(r.sellUnitsInSeason),
    }),
  },
  {
    id: 'eficacia',
    label1: 'Eficacia',
    label2: 'Campaña',
    align: 'right',
    width: 95,
    sortBy: 'efficacyTotal',
    render: (r) => ({
      line1: formatPct(r.efficacyTotal),
      line2: formatPct(r.efficacyInSeason),
      line1Negative: isNeg(r.efficacyTotal),
      line2Negative: isNeg(r.efficacyInSeason),
    }),
  },
];

export const colById = (id) => SERIES_COLUMNS.find((c) => c.id === id);

// Paleta + métricas comunes a todas las tablas del report — viven en
// `./tableStyles`. Mantenemos aquí los alias cortos para no tocar el
// resto del fichero.
const CELL_BORDER = TABLE_CELL_BORDER;
const POSITIVE = TABLE_POSITIVE;
const DANGER = TABLE_DANGER;
const SUBTLE = TABLE_SUBTLE;
const cellPadding = TABLE_CELL_PADDING;

/** Línea 1 del header con barra superior fina sobre `barText` para
 *  marcar la columna como agregado/promedio (`x̄`). Asume que `barText`
 *  está al INICIO del label. */
const OverbarLine1 = ({ label, barText }) => {
  if (!label.startsWith(barText)) return <span>{label}</span>;
  const rest = label.slice(barText.length);
  return (
    <>
      <span style={{ borderTop: '0.5px solid currentColor', paddingTop: 1, display: 'inline-block' }}>{barText}</span>
      {rest}
    </>
  );
};

const SeriesHeaderCell = ({ col, sortBy, sortDir, onSort, sticky }) => {
  const sortable = !!col.sortBy;
  const isActive = sortable && sortBy === col.sortBy;
  const arrow = isActive ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  return (
    <th
      onClick={sortable ? () => onSort(col.sortBy) : undefined}
      className={sticky?.className}
      style={{
        ...cellPadding,
        fontSize: 12,
        // Activo: peso mayor. El color del título siempre oscuro
        // para que se lea bien sobre el gris del header.
        fontWeight: isActive ? 700 : 600,
        textAlign: col.align,
        whiteSpace: 'nowrap',
        cursor: sortable ? 'pointer' : 'default',
        userSelect: 'none',
        backgroundColor: TABLE_HEADER_BG,
        color: TABLE_HEADER_TEXT_STRONG,
        width: col.width,
        minWidth: col.width,
        maxWidth: col.width,
        verticalAlign: 'top',
        borderBottom: CELL_BORDER,
        ...(sticky?.style || {}),
      }}
      title={sortable ? 'Ordenar por esta columna' : undefined}
    >
      <div>
        {col.meanBar ? <OverbarLine1 label={col.label1} barText={col.meanBar} /> : col.label1}
        {arrow}
      </div>
      {col.label2 && (
        <div
          style={{
            fontSize: 12,
            fontWeight: 400,
            color: TABLE_HEADER_TEXT_SOFT,
            marginTop: 0,
            lineHeight: 1.2,
          }}
        >
          ({col.label2})
        </div>
      )}
    </th>
  );
};

/** Pinta una celda del cuerpo. Para imágenes, foto cuadrada con
 *  `object-fit: cover`. Para el resto, hasta 2 líneas (line1 en negro
 *  con bold rojo si negativo, line2 en gris entre paréntesis). El dot
 *  se ancla al borde izquierdo de la celda para que todos los puntos
 *  de la columna queden alineados verticalmente. */
const SeriesBodyCell = ({ col, row, ctx, sticky }) => {
  const cell = col.render(row, ctx);
  if (cell?.kind === 'image') {
    return (
      <td
        className={sticky?.className}
        style={{
          padding: 8,
          textAlign: col.align,
          borderBottom: CELL_BORDER,
          whiteSpace: 'nowrap',
          width: col.width,
          minWidth: col.width,
          maxWidth: col.width,
          ...(sticky?.style || {}),
        }}
      >
        {cell.src ? (
          <img
            src={cell.src}
            alt={cell.alt}
            style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6, display: 'block' }}
          />
        ) : (
          <Box minWidth="72px" minHeight="72px" />
        )}
      </td>
    );
  }
  const hasTwo = cell?.line2 !== undefined && cell?.line2 !== '';
  // Si la celda lleva dot, reservamos espacio a la derecha para que no se
  // solape con el número alineado a la derecha.
  const tdStyle = {
    ...cellPadding,
    ...(cell?.dot ? { paddingRight: 28 } : null),
    textAlign: col.align,
    borderBottom: CELL_BORDER,
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    position: 'relative',
    width: col.width,
    minWidth: col.width,
    maxWidth: col.width,
    color: TABLE_BODY_TEXT,
    ...(sticky?.style || {}),
  };
  return (
    <td className={sticky?.className} style={tdStyle}>
      {cell?.dot && (
        <span
          style={{
            position: 'absolute',
            right: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: cell.dot === 'green' ? POSITIVE : DANGER,
          }}
        />
      )}
      <div
        style={{
          color: cell?.line1Negative ? DANGER : TABLE_BODY_TEXT,
          fontWeight: cell?.line1Negative ? 600 : 400,
          fontSize: 13,
          lineHeight: 1.4,
        }}
      >
        {cell?.line1 ?? '—'}
      </div>
      {hasTwo && (
        <div
          style={{
            color: cell?.line2Negative ? DANGER : SUBTLE,
            fontWeight: cell?.line2Negative ? 600 : 400,
            fontSize: 12,
            lineHeight: 1.3,
            marginTop: 2,
          }}
        >
          ({cell.line2})
        </div>
      )}
    </td>
  );
};

/**
 * Construye la fila TOTAL a partir de las filas visibles + metadata.
 * Mismo álgebra que el PDF `pdfEfficacySeries`:
 *   - sumas para uds y €
 *   - PVP/ud medio agregado = Σ pvp_cobrado / Σ uds_vendidas
 *   - Dto. agregado = 1 − Σ pvp_cobrado / Σ (pvp_etiqueta · uds)
 *   - Eficacia agregada = Σ uds_vendidas / Σ uds_compradas
 *
 * Recibe `rows` (no sólo metadata) porque necesitamos `pvpUnit` por
 * familia para ponderar el descuento.
 */
const buildTotalRow = (rows) => {
  if (!rows || rows.length === 0) return null;
  const t = {
    buyUnits: 0,
    buyValue: 0,
    sellValuePvfTotal: 0,
    sellUnitsTotal: 0,
    sellUnitsInSeason: 0,
    sellValuePvpTotal: 0,
    sellValuePvpInSeason: 0,
    pvpEtiquetaWeightedTotal: 0,
    pvpEtiquetaWeightedInSeason: 0,
  };
  for (const r of rows) {
    const bu = Number(r.buyUnits) || 0;
    const bv = Number(r.buyValue) || 0;
    const pvp = typeof r.pvpUnit === 'number' && Number.isFinite(r.pvpUnit) ? r.pvpUnit : null;
    const sut = Number(r.sellUnitsTotal) || 0;
    const sui = Number(r.sellUnitsInSeason) || 0;
    t.buyUnits += bu;
    t.buyValue += bv;
    t.sellValuePvfTotal += Number(r.sellValuePvfTotal) || 0;
    t.sellUnitsTotal += sut;
    t.sellUnitsInSeason += sui;
    t.sellValuePvpTotal += Number(r.sellValuePvpTotal) || 0;
    t.sellValuePvpInSeason += Number(r.sellValuePvpInSeason) || 0;
    if (pvp !== null) {
      t.pvpEtiquetaWeightedTotal += pvp * sut;
      t.pvpEtiquetaWeightedInSeason += pvp * sui;
    }
  }
  return {
    family: 'TOTAL',
    image: null,
    firstStoreEntryDate: null,
    pvpUnit: null,
    buyUnits: t.buyUnits,
    buyValue: t.buyValue,
    sellValuePvfTotal: t.sellValuePvfTotal,
    pvpAvgUnitTotal: div(t.sellValuePvpTotal, t.sellUnitsTotal),
    pvpAvgUnitInSeason: div(t.sellValuePvpInSeason, t.sellUnitsInSeason),
    discountAvgTotal: t.pvpEtiquetaWeightedTotal > 0 ? 1 - t.sellValuePvpTotal / t.pvpEtiquetaWeightedTotal : null,
    discountAvgInSeason:
      t.pvpEtiquetaWeightedInSeason > 0 ? 1 - t.sellValuePvpInSeason / t.pvpEtiquetaWeightedInSeason : null,
    sellUnitsTotal: t.sellUnitsTotal,
    sellUnitsInSeason: t.sellUnitsInSeason,
    efficacyTotal: div(t.sellUnitsTotal, t.buyUnits),
    efficacyInSeason: div(t.sellUnitsInSeason, t.buyUnits),
    // Auxiliares (los lleva el row aunque las columnas no los pinten,
    // por si la fila TOTAL se usa en otro lado más adelante).
    sellValuePvpTotal: t.sellValuePvpTotal,
    sellValuePvpInSeason: t.sellValuePvpInSeason,
  };
};

/**
 * Tabla del informe Series — gemela del PDF
 * `pdfEfficacySeries.service.ts`. Header plano de 2 líneas (sin grupos),
 * Foto + Serie-Color sticky a la izquierda, fila TOTAL al pie con
 * agregados ponderados. Ordenable por las columnas que el back acepta.
 */
export const SeriesTable = ({ rows, timezone, sortBy, sortDir, onSort }) => {
  const { currency } = useCurrency();
  const ctx = { currency, timezone };
  // Expandida: quitamos el `max-height` interno y dejamos que la
  // tabla crezca todo lo que necesite (scroll natural de la página).
  // Pierde el efecto de sticky vertical en thead/tfoot — el scroll
  // ya no es del contenedor — pero a cambio la tabla deja de estar
  // "encajonada". El usuario elige.
  const [expanded, setExpanded] = useState(false);

  // Sticky por columna: acumula el `left` para que cada sticky empuje a
  // la siguiente. La última sticky recibe la sombra de borde fijo.
  let stickyAcc = 0;
  let lastStickyIdx = -1;
  const enriched = SERIES_COLUMNS.map((c, i) => {
    if (!c.sticky) return { ...c };
    const stickyLeft = stickyAcc;
    stickyAcc += c.width;
    lastStickyIdx = i;
    return { ...c, stickyLeft };
  });
  if (lastStickyIdx >= 0) enriched[lastStickyIdx].isLastSticky = true;

  // Cada celda puede estar "sticky" en dos ejes: horizontal (columnas
  // foto / serie-color, siempre visibles al hacer scroll lateral) y
  // vertical (toda la fila del header arriba; toda la fila TOTAL
  // abajo). Cada combinación tiene su pequeño tweak de z-index para
  // que las esquinas (sticky H + V) queden por encima de los demás
  // sticky.
  const SHADOW_RIGHT = '4px 0 6px -3px rgba(0,0,0,0.12)';

  const headerStickyAttrs = (c) => {
    const isCol = !!c.sticky;
    return {
      className: isCol ? 'efftable-sticky' : undefined,
      style: {
        position: 'sticky',
        top: 0,
        ...(isCol ? { left: c.stickyLeft } : {}),
        zIndex: isCol ? 4 : 3,
        backgroundColor: TABLE_HEADER_BG,
        ...(c.isLastSticky && isCol ? { boxShadow: SHADOW_RIGHT } : {}),
      },
    };
  };

  const bodyStickyAttrs = (c) => {
    if (!c.sticky) return null;
    return {
      // CSS heredará el bg de la fila (`.efftable-row`) — así stripe y
      // hover se aplican también a las columnas sticky.
      className: 'efftable-sticky',
      style: {
        position: 'sticky',
        left: c.stickyLeft,
        zIndex: 2,
        ...(c.isLastSticky ? { boxShadow: SHADOW_RIGHT } : {}),
      },
    };
  };

  const footerStickyAttrs = (c) => {
    const isCol = !!c.sticky;
    return {
      className: isCol ? 'efftable-sticky' : undefined,
      style: {
        position: 'sticky',
        bottom: 0,
        ...(isCol ? { left: c.stickyLeft } : {}),
        zIndex: isCol ? 4 : 3,
        backgroundColor: TABLE_TOTAL_BG,
        ...(c.isLastSticky && isCol ? { boxShadow: SHADOW_RIGHT } : {}),
      },
    };
  };

  const totalRow = buildTotalRow(rows);

  return (
    <>
      {/* Toggle Expandir/Contraer alineado a la derecha justo encima
          de la tabla. Cuando se expande, la tabla deja de scrollearse
          internamente y crece todo lo que pida — útil para
          presentar/imprimir, a cambio de perder thead/tfoot pegados. */}
      <Box paddingBlockEnd="2">
        <HorizontalStack align="end">
          <Button
            plain
            size="slim"
            icon={expanded ? ChevronUpMinor : ChevronDownMinor}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? 'Contraer tabla' : 'Expandir tabla'}
          </Button>
        </HorizontalStack>
      </Box>
      {/* Marco + CSS comunes vienen del <TableShell> compartido. */}
      <TableShell>
      {/* Scroll en los dos ejes: el thead queda fijo arriba y el
          tfoot abajo mientras se navega por el cuerpo. El alto se
          calcula como viewport menos lo que ocupan TopBar + Page
          header + FiltersCard + título-chips + tab-bar (≈ 240px).
          El `max(...)` evita que en pantallas chicas se quede
          demasiado pequeña. Expandida: sin tope — la tabla crece
          tanto como necesite y el scroll lo gestiona la página. */}
      <div
        style={{
          overflow: 'auto',
          maxHeight: expanded ? 'none' : 'max(440px, calc(100vh - 240px))',
        }}
      >
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
          <thead>
            <tr>
              {enriched.map((c) => (
                <SeriesHeaderCell
                  key={c.id}
                  col={c}
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                  sticky={headerStickyAttrs(c)}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.family} className="efftable-row">
                {enriched.map((c) => (
                  <SeriesBodyCell key={c.id} col={c} row={r} ctx={ctx} sticky={bodyStickyAttrs(c)} />
                ))}
              </tr>
            ))}
          </tbody>
          {totalRow && (
            <tfoot>
              <tr style={{ fontWeight: 600 }}>
                {enriched.map((c) => (
                  <SeriesBodyCell key={c.id} col={c} row={totalRow} ctx={ctx} sticky={footerStickyAttrs(c)} />
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      </TableShell>
    </>
  );
};
